import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import useActiveSection from "active-section";
import SEO from "components/SEO/SEO.js";
import GeneNavbar from "components/Navbars/GeneNavbar.js";
import DemoFooter from "components/Footers/DemoFooter.js";
import { Button, Container, Row, Col } from "reactstrap";
import { RESOURCE_GROUPS, RESOURCES_SEO, RESOURCES_PUBLISHED, hostOf } from "./resources-config";

// The section rhythm, rule, title and jump nav are the service pages', reused
// here — hence `service-detail` on the wrapper. `tint` is this page's own tone
// rather than a reuse of ServicePage's `lift`, which means "a section whose
// content is a white card and needs the tint behind it to read as raised". Same
// pixels today, different reasons; keeping them separate means one can change.
const TONE_CLASS = {
  hero: "service-section--hero",
  plain: "",
  tint: "resources-section--tint",
  cta: "service-section--cta",
};

// Below this a jump nav costs more than the scrolling it saves. Deliberately
// per-page rather than shared with ServicePage.js, which declares the same
// number for the same reason: the two are free to disagree about when a jump
// nav earns its space. resources-config.test.js pins the group count above this
// one, so dropping a group fails there rather than silently removing the nav.
const MIN_NAV_ENTRIES = 3;

// A link that leaves the site says so twice: this arrow for the reader who can
// see it, and the visually hidden text beside it for the reader who cannot. It
// takes currentColor rather than the brand teal — teal is a fill colour here
// because it does not clear 4.5:1 on white, and this glyph carries meaning.
function ExternalLinkArrow() {
  return (
    <svg
      className="resource-external"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M4.75 1.75h5.5v5.5M10.25 1.75L5.5 6.5M8.75 7v3.25H1.75V3.25H5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ResourcesPage() {
  const { t } = useTranslation();
  const { search } = useLocation();

  // Where a visit to this page begins — the top, the reader's last position, or
  // the group named in the URL — is settled by <ScrollRestoration> in routes.js
  // for every page at once. Clearance under the fixed navbar is
  // `scroll-margin-top` in the stylesheet.

  const sections = [
    {
      id: "hero",
      tone: "hero",
      content: (
        <Container>
          <Row>
            <Col md="10">
              {/* This page has no GeneHeader, so it supplies the document's
                  only h1. */}
              <h1 className="service-hero-title">{t("resources.hero.title")}</h1>
              <p className="service-hero-lede">{t("resources.hero.lede")}</p>
            </Col>
          </Row>
        </Container>
      ),
    },
    ...RESOURCE_GROUPS.map((group) => ({
      id: group.id,
      tone: group.tone,
      navLabel: t(`resources.group.${group.id}.title`),
      content: (
        <Container>
          <Row>
            <Col md="10">
              {/* Two columns from 992px up — the title in a rail, the links
                  beside it — and one stacked column below that. The wrapper is
                  what the grid is declared on; see _resources.scss. */}
              <div className="resource-group">
                <div className="resource-group-head">
                  <span className="service-rule" aria-hidden="true" />
                  <h2 className="service-section-title">
                    {t(`resources.group.${group.id}.title`)}
                  </h2>
                </div>
                {/* A list, so a screen reader announces how many resources are
                    here before reading the first one. The description sits
                    beside the link rather than inside it: a link's accessible
                    name should be the destination, not a sentence about it. */}
                <ul className="resource-list">
                  {group.entries.map((entry) => (
                    <li className="resource-item" key={entry.key}>
                      <a
                        className="resource-link"
                        href={entry.url}
                        target="_blank"
                        // `noopener` is implied by `_blank` in current browsers
                        // and written out anyway; the commented-out footer
                        // links this page replaces set `_blank` with no `rel`
                        // at all.
                        rel="noopener noreferrer"
                      >
                        <span className="resource-name">{entry.name}</span>
                        {/* Host and arrow are wrapped together because a
                            replaced element opens a line-break opportunity
                            beside it, and on the longest name the arrow was
                            landing alone on a line of its own, reading as a
                            rendering fault. */}
                        <span className="resource-target">
                          <span className="resource-host">
                            {hostOf(entry.url)}
                          </span>
                          <ExternalLinkArrow />
                        </span>
                        <span className="sr-only">
                          {" "}
                          ({t("resources.opensInNewTab")})
                        </span>
                      </a>
                      <p className="resource-note">
                        {t(`resources.group.${group.id}.entry.${entry.key}`)}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </Col>
          </Row>
        </Container>
      ),
    })),
    {
      // Deliberately carries no navLabel. The jump nav stays a list of topics;
      // an entry for the small print would be the one item in it that is not
      // something the reader came to read.
      id: "disclaimer",
      tone: "plain",
      content: (
        <Container>
          <Row>
            <Col md="10">
              <div className="resources-disclaimer">
                {/* A real heading rather than a styled paragraph, so heading
                    navigation finds it. */}
                <h2 className="resources-disclaimer-title">
                  {t("resources.disclaimer.title")}
                </h2>
                <p>{t("resources.disclaimer.body")}</p>
              </div>
            </Col>
          </Row>
        </Container>
      ),
    },
    {
      id: "contact",
      tone: "cta",
      content: (
        <Container>
          <Row>
            <Col className="ml-auto mr-auto text-center" md="8">
              <h2 className="service-cta-title">{t("landing.haveQuestions")}</h2>
              <p className="service-cta-lede">{t("landing.contactPrompt")}</p>
              <Button
                className="btn-fill btn-round"
                color="info"
                size="lg"
                tag={Link}
                to={{ pathname: "/index", hash: "#contact" }}
              >
                {t("landing.getInTouch")}
              </Button>
            </Col>
          </Row>
        </Container>
      ),
    },
  ];

  const navEntries = sections.filter((section) => section.navLabel);
  const showNav = navEntries.length >= MIN_NAV_ENTRIES;
  // useActiveSection keys its effect on the joined ids, so a fresh array
  // identity each render costs nothing. The reset key is a constant: this page's
  // id list never changes, and switching language changes the labels but not the
  // ids, so there is nothing to reset on.
  const activeId = useActiveSection(
    navEntries.map((entry) => entry.id),
    "resources"
  );
  // The nav goes above the first section that appears in it, wherever that
  // lands — ServicePage names its own insertion point, which only holds because
  // its section order is fixed.
  const navAnchorId = navEntries.length > 0 ? navEntries[0].id : null;

  return (
    <>
      <SEO
        // Just the page name: SEO.js appends the brand suffix itself.
        title={RESOURCES_SEO.title}
        description={RESOURCES_SEO.description}
        keywords={RESOURCES_SEO.keywords}
        canonicalUrl={RESOURCES_SEO.canonicalUrl}
        ogUrl={RESOURCES_SEO.ogUrl}
        noindex={!RESOURCES_PUBLISHED}
        // og:title falls back to the bare `title` with no suffix, so it is
        // given the brand explicitly.
        ogTitle={`${RESOURCES_SEO.title} | GeneFlou Genetic Counselling`}
        ogDescription={RESOURCES_SEO.description}
      />
      <GeneNavbar />
      <div className="main service-detail resources-page">
        {sections.map((section) => (
          <React.Fragment key={section.id}>
            {section.id === navAnchorId && showNav && (
              <nav
                className="service-jump"
                // Named with the service pages' string: it is the same landmark
                // doing the same job, and one translated name beats two.
                aria-label={t("landing.serviceSection.onThisPage")}
              >
                <Container>
                  {/* <Link>, not a bare <a href="#...">. A plain fragment link
                      is a navigation the browser makes on its own: it pushes a
                      history entry the router never learns about, and since
                      <ScrollRestoration> has taken restoration off the browser,
                      going back to one moves the URL and nothing else. */}
                  <ul>
                    {navEntries.map((entry) => (
                      <li key={entry.id}>
                        <Link
                          // `search` is carried explicitly: a `to` of just a
                          // fragment keeps the path but drops the query, which
                          // would strip campaign parameters off a shared link
                          // the moment the reader used the nav.
                          to={{ hash: `#${entry.id}`, search }}
                          className={
                            entry.id === activeId ? "is-active" : undefined
                          }
                          aria-current={
                            entry.id === activeId ? "true" : undefined
                          }
                        >
                          {entry.navLabel}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </Container>
              </nav>
            )}
            <section
              id={section.id}
              className={`section service-section ${TONE_CLASS[section.tone]}`}
            >
              {section.content}
            </section>
          </React.Fragment>
        ))}
      </div>
      <DemoFooter />
    </>
  );
}

export default ResourcesPage;
